import React, {useState, useEffect} from "react";
import {observer} from "mobx-react-lite";
import {CustomerFluidSubPageLayout} from "../../layouts/customerLayout/customerFluidSubPageLayout/CustomerFluidSubPageLayout";
import {CustomerMenuHeader} from "components/layouts/customerLayout/components/customerMenuHeader";
import {Tabs, message} from "antd";
import {FileTextOutlined, CalendarOutlined} from "@ant-design/icons";
import CustomerReportsFilters from "./components/CustomerReportsFilters";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {b64DecodeToUrl} from "../../../utils/helpers";
import moment from "moment";
import {ReportsContainer, StyledTabs} from "./CustomerReportsPage.styles";

const {TabPane} = Tabs;

const CustomerReportsPage = () => {
    const stores = useStore();

    const isPoolCustomer = stores?.meStore?.isPoolCustomer;
    const isCustomerUser = stores?.meStore?.role === "cUser";
    const [orderStartDate, setOrderStartDate] = useState(
        moment()
            .subtract(1, "days")
            .format("YYYY-MM-DD")
    );
    const [orderEndDate, setOrderEndDate] = useState(
        moment()
            .subtract(1, "days")
            .format("YYYY-MM-DD")
    );
    const [shiftStartDate, setShiftStartDate] = useState(
        moment()
            .subtract(1, "days")
            .format("YYYY-MM-DD")
    );
    const [shiftEndDate, setShiftEndDate] = useState(
        moment()
            .subtract(1, "days")
            .format("YYYY-MM-DD")
    );
    const [orderPeriod, setOrderPeriod] = useState("yesterday");
    const [shiftPeriod, setShiftPeriod] = useState("yesterday");
    const [orderSubCompany, setOrderSubCompany] = useState(
        stores?.meStore?.selectedSubCompanyId ? [stores.meStore.selectedSubCompanyId] : []
    );
    const [shouldFetchOrderReport, setShouldFetchOrderReport] = useState(false);
    const [shouldFetchShiftReport, setShouldFetchShiftReport] = useState(false);
    const [shiftSubCompany, setShiftSubCompany] = useState(
        stores?.meStore?.selectedSubCompanyId ? [stores.meStore.selectedSubCompanyId] : []
    );
    const [isOrderDatePickerExpanded, setIsOrderDatePickerExpanded] = useState(false);
    const [isShiftDatePickerExpanded, setIsShiftDatePickerExpanded] = useState(false);
    const hasMultipleSubcompanies = stores?.meStore?.subcompanies?.length > 1;

    const [orderReportLoading, orderReportData, , , refetchOrderReport] = useFetch({
        fetchFn: () => {
            const requestData = {
                startDate: moment(orderStartDate)
                    .startOf("day")
                    .toISOString(),
                endDate: moment(orderEndDate)
                    .endOf("day")
                    .toISOString(),
                companies: [stores?.meStore?.companies?.[0]?._id],
                workType: "all",
                city: null,
                subcompanies: orderSubCompany
            };
            return stores.orderStore.getOrdersReportForCompany(requestData);
        },
        dependencies: [shouldFetchOrderReport],
        condition: shouldFetchOrderReport,
        initialCall: false,
        callbackFunc: async response => {
            if (response && response.data) {
                try {
                    const base64Data = response.data;
                    const blobUrl = await b64DecodeToUrl(base64Data);
                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.setAttribute(
                        "download",
                        `siparis-raporu-${moment().format("YYYY-MM-DD")}.xlsx`
                    );
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    message.success("Sipariş raporu başarıyla indirildi!");
                } catch (error) {
                    message.error("Excel dosyası indirilirken bir hata oluştu!");
                }
            }
            setShouldFetchOrderReport(false);
        }
    });

    const [shiftReportData, , , , refetchShiftReport] = useFetch({
        fetchFn: () => {
            return stores.courierStore.getShiftsReport({
                startDate: moment(shiftStartDate)
                    .startOf("day")
                    .toISOString(),
                endDate: moment(shiftEndDate)
                    .endOf("day")
                    .toISOString(),
                subCompanyIds: shiftSubCompany
            });
        },
        initialCall: false,
        dependencies: [shouldFetchShiftReport],
        condition: shouldFetchShiftReport,
        callbackFunc: async response => {
            if (response) {
                try {
                    const blobUrl = response;
                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.setAttribute(
                        "download",
                        `vardiya-puantaj-raporu-${moment().format("YYYY-MM-DD")}.xlsx`
                    );
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    message.success("Vardiya puantaj raporu başarıyla indirildi!");
                } catch (error) {
                    message.error("Excel dosyası indirilirken bir hata oluştu!");
                }
            }
            setShouldFetchShiftReport(false);
        }
    });

    const handleOrderReportDownload = () => {
        setShouldFetchOrderReport(true);
    };

    const handleShiftReportDownload = async () => {
        setShouldFetchShiftReport(true);
    };

    const getAvailableTabs = () => {
        if (isPoolCustomer) {
            return [
                {
                    key: "1",
                    tab: (
                        <span>
                            <FileTextOutlined />
                            Sipariş Raporu
                        </span>
                    ),
                    content: (
                        <>
                            <CustomerReportsFilters
                                period={orderPeriod}
                                setPeriod={setOrderPeriod}
                                startDate={orderStartDate}
                                setStartDate={setOrderStartDate}
                                endDate={orderEndDate}
                                setEndDate={setOrderEndDate}
                                showAllTime={false}
                                showNextButton={false}
                                showPrevButton={false}
                                hasMultipleSubcompanies={hasMultipleSubcompanies}
                                company={stores?.meStore?.companies?.[0]?._id}
                                subCompany={orderSubCompany}
                                setSubCompany={setOrderSubCompany}
                                isDatePickerExpanded={isOrderDatePickerExpanded}
                                setIsDatePickerExpanded={setIsOrderDatePickerExpanded}
                                handleOrderReportDownload={handleOrderReportDownload}
                                orderReportLoading={orderReportLoading}
                                handleShiftReportDownload={handleShiftReportDownload}
                                reportType="order"
                            />
                        </>
                    )
                }
            ];
        } else if (isCustomerUser && !isPoolCustomer) {
            return [
                {
                    key: "1",
                    tab: (
                        <span>
                            <CalendarOutlined />
                            Vardiya Puantaj Raporu
                        </span>
                    ),
                    content: (
                        <>
                            <CustomerReportsFilters
                                period={shiftPeriod}
                                setPeriod={setShiftPeriod}
                                startDate={shiftStartDate}
                                setStartDate={setShiftStartDate}
                                endDate={shiftEndDate}
                                setEndDate={setShiftEndDate}
                                showAllTime={false}
                                hasMultipleSubcompanies={hasMultipleSubcompanies}
                                company={stores?.meStore?.companies?.[0]?._id}
                                subCompany={shiftSubCompany}
                                setSubCompany={setShiftSubCompany}
                                isDatePickerExpanded={isShiftDatePickerExpanded}
                                setIsDatePickerExpanded={setIsShiftDatePickerExpanded}
                                handleOrderReportDownload={handleOrderReportDownload}
                                orderReportLoading={orderReportLoading}
                                handleShiftReportDownload={handleShiftReportDownload}
                                reportType="shift"
                            />
                        </>
                    )
                },
                {
                    key: "2",
                    tab: (
                        <span>
                            <FileTextOutlined />
                            Sipariş Raporu
                        </span>
                    ),
                    content: (
                        <>
                            <CustomerReportsFilters
                                period={orderPeriod}
                                setPeriod={setOrderPeriod}
                                startDate={orderStartDate}
                                setStartDate={setOrderStartDate}
                                endDate={orderEndDate}
                                setEndDate={setOrderEndDate}
                                showAllTime={false}
                                showNextButton={false}
                                showPrevButton={false}
                                hasMultipleSubcompanies={hasMultipleSubcompanies}
                                company={stores?.meStore?.companies?.[0]?._id}
                                subCompany={orderSubCompany}
                                setSubCompany={setOrderSubCompany}
                                isDatePickerExpanded={isOrderDatePickerExpanded}
                                setIsDatePickerExpanded={setIsOrderDatePickerExpanded}
                                handleOrderReportDownload={handleOrderReportDownload}
                                orderReportLoading={orderReportLoading}
                                handleShiftReportDownload={handleShiftReportDownload}
                                reportType="order"
                            />
                        </>
                    )
                }
            ];
        }
    };

    const availableTabs = getAvailableTabs();

    return (
        <CustomerFluidSubPageLayout>
            <CustomerMenuHeader />
            <ReportsContainer>
                <StyledTabs defaultActiveKey="1" size="large">
                    {availableTabs.map(tab => (
                        <TabPane key={tab.key} tab={tab.tab}>
                            {tab.content}
                        </TabPane>
                    ))}
                </StyledTabs>
            </ReportsContainer>
        </CustomerFluidSubPageLayout>
    );
};

export default observer(CustomerReportsPage);
